/**
 * ============================================================================
 * BB Cart — cart page + drawer (separate from builder)
 * ============================================================================
 *
 * SURFACE: /cart (+ ajax cart drawer)
 * BUILDER submit lives in box-builder.js → BBCart.submit()
 *
 * GROUPING
 *   Lines with properties._bb_box_id are rendered as one gift box
 *   (snippets/bb-cart-box.liquid). Edit/duplicate rehydrate builder
 *   via sessionStorage + ?edit=boxId.
 *
 * TARGETS
 *   [data-bb-cart]              cart root
 *   [data-bb-cart-box]          grouped box accordion
 *   [data-bb-cart-modal]        card edit / send-to-another modal
 *   CSS: assets/bb-cart.css     (.bb-cart*, .bb-cart-box*, .bb-cart-modal*)
 *
 * MOBILE NOTE
 *   Prefer extending existing @media (max-width: 899px / 749px) rules
 *   in bb-cart.css before adding cart-specific JS layout modes.
 */
(function () {
  "use strict";
  if (window.__bbCartInit) return;
  window.__bbCartInit = true;

  var SECTIONS = "section-ajax-cart,main-cart";
  var modalState = {
    mode: "edit",
    boxId: "",
    payload: null,
    selectedVariantId: null,
    skip: false
  };

  function qs(sel, root) {
    return (root || document).querySelector(sel);
  }

  function qsa(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function cartRoot() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
    return root.slice(-1) === "/" ? root : root + "/";
  }

  function stateFromPayload(payload) {
    var hydrate = (payload && payload.hydrate) || {};
    var items = {};
    (hydrate.items || []).forEach(function (item) {
      if (!item || !item.variantId) return;
      items[String(item.variantId)] = {
        productId: item.productId,
        variantId: item.variantId,
        handle: item.handle || "",
        title: item.title || "",
        vendor: item.vendor || "",
        price: Number(item.price) || 0,
        size: item.size == null || item.size === "" ? 1 : Number(item.size) || 1,
        image: item.image || "",
        tags: item.tags || [],
        qty: Math.max(1, Number(item.qty) || 1),
        source: item.source || "goodies",
        available: item.available !== false
      };
    });
    var box = hydrate.box || null;
    if (box) {
      box.price = Number(box.price) || 0;
      box.size = 0;
    }
    var card = hydrate.card || null;
    if (card) {
      card.price = Number(card.price) || 0;
      card.saved = true;
      card.blank = !!card.blank;
      card.properties = card.properties || {};
    }
    return {
      version: 1,
      step: 1,
      occasion: null,
      boxIndex: parseInt(hydrate.boxN, 10) || 1,
      boxId: payload.boxId,
      items: items,
      box: box,
      card: card,
      skipCard: !!hydrate.skipCard || !card,
      editing: true,
      editCopies: Math.max(1, Number(payload.copies) || 1),
      completed: [],
      updatedAt: Date.now()
    };
  }

  function beginEditBox(boxEl, href) {
    var payload = parsePayload(boxEl);
    if (!payload || !payload.boxId) {
      if (href) window.location.href = href;
      return;
    }
    try {
      sessionStorage.setItem("bb_box_v1", JSON.stringify(stateFromPayload(payload)));
      sessionStorage.removeItem(MS_LEAVE_KEY);
    } catch (e) {}
    document.documentElement.removeAttribute("data-bb-ms-leaving");
    window.location.href = href || "/pages/corporate-gifts?view=box-builder-app";
  }

  /* TWO PAYLOAD SHAPES, ONE READER. A builder box card carries
     [data-bb-box-payload]; a prebuilt one carries [data-bb-prebuilt-payload]
     (snippets/bb-cart-prebuilt.liquid). Both state a `lines` array naming
     every cart line the card owns, which is all removeGroup() reads -- so
     falling back here is what lets ONE group-remove serve both kinds instead
     of a second copy of it written for prebuilt boxes.

     The builder shape is tried FIRST and the two are never both present on one
     card, so nothing about a builder box changes. Callers that read
     builder-only fields -- setGroupCopies' line.unitQty, the card-modal
     trigger's cardVariantId -- are reached only from controls a prebuilt card
     does not render, so they cannot land on the wrong shape.

     The prebuilt payload's string VALUES arrive HTML-escaped (the json filter
     does that, see the note in the snippet) and the editor undoes it with
     deepDecode. Nothing here needs that: a cart line key is hex and a colon,
     with nothing in it to escape. Do not read text fields off this without
     decoding them first. */
  function parsePayload(boxEl) {
    var node = qs("[data-bb-box-payload]", boxEl) || qs("[data-bb-prebuilt-payload]", boxEl);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  function alpineCart() {
    try {
      return window.Alpine && window.Alpine.store && window.Alpine.store("cart");
    } catch (e) {
      return null;
    }
  }

  /* LAST RESORT ONLY. store.reload() reloads the whole document on /cart:
     main-cart.liquid renders the cart as plain server-side Liquid with nothing
     bound to the store, unlike sections/section-ajax-cart.liquid whose body is
     x-html="$store.cart.cartAjaxHtml". Since store.reload IS a function here,
     the window.location.reload() below is NOT what fires -- the reload comes
     from inside the store. Every cart mutation should go through mutateCart /
     renderCartInPlace instead; this stays only for surfaces with no markup of
     ours to swap. */
  function refreshCart() {
    var store = alpineCart();
    if (store && typeof store.reload === "function") {
      store.reload();
      return;
    }
    window.location.reload();
  }

  function setBusy(on) {
    qsa("[data-bb-cart]").forEach(function (el) {
      el.classList.toggle("is-busy", !!on);
    });
    var store = alpineCart();
    if (store) store.loading = !!on;
  }

  function postJson(path, body) {
    return fetch(cartRoot() + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(function (res) {
      if (!res.ok) throw new Error(path + " failed");
      return res.json();
    });
  }

  /* ----- in-place cart render -----
     Every mutation here already posts `sections`, so Shopify returns the
     re-rendered HTML for both cart surfaces in the response. That HTML used to
     be discarded in favour of refreshCart(), which reloads the document.

     Only .bb-cart-page is swapped, never the whole section: main-cart.liquid
     also renders bb-cart-modal, bb-cart-prebuilt-modal and bb-cart-ship-terms,
     and openModal/openFor REPARENT those to <body>. Swapping the section
     wholesale would leave a second copy of each modal in the document and
     getModal()/modal() would start resolving the wrong one. */
  function onCartPage() {
    return !!qs(".bb-cart-page");
  }

  function renderCartInPlace(res) {
    var html = res && res.sections && res.sections["main-cart"];
    if (typeof html !== "string" || !html) return false;
    var current = qs(".bb-cart-page");
    if (!current || !current.parentNode) return false;
    var next;
    try {
      next = new DOMParser().parseFromString(html, "text/html").querySelector(".bb-cart-page");
    } catch (e) {
      return false;
    }
    if (!next) return false;
    current.parentNode.replaceChild(document.importNode(next, true), current);
    return true;
  }

  /* THE DRAWER'S EQUIVALENT -- and the reason removing a box used to shut it.

     renderCartInPlace only knows the cart PAGE: it looks for .bb-cart-page,
     which does not exist in the drawer, so in there it returned false for
     every mutation and applyOrReload fell through to refreshCart(), which
     reloads the document. A reload closes the drawer.

     Most drawer controls never hit that, because changeLine() hands a
     single-line change to the store's own changeById(). removeGroup() cannot
     -- it is one multi-line cart/update.js -- so removing a box card was the
     one drawer action that reloaded, and it looked like the cart closing
     itself at random.

     Nothing extra is fetched: every mutation already posts `sections`, so the
     response carries this section's freshly rendered HTML. The drawer body is
     x-html="$store.cart.cartAjaxHtml" (sections/section-ajax-cart.liquid), so
     handing the store that element's innerHTML re-renders the body the same
     way the store's own path does.

     [data-cart-body] IS the element carrying that x-html -- not a wrapper, so
     innerHTML is exactly what the binding expects. The foot sits OUTSIDE it
     and is deliberately not re-rendered; it keeps itself in step from the
     mirror spans inside the body, which this swap replaces along with
     everything else, and the section's own observer copies them out.

     IT USED TO SET cartAjaxHtml AND NOTHING ELSE, with a note saying that was
     enough "unless a future change starts reading a COUNT or TOTAL off the
     store itself rather than off the rendered cart". That change had already
     happened: the pinned foot is

         <div class="bb-cart-drawer__foot" x-show="$store.cart.itemsCount > 0">

     and it sits OUTSIDE the x-html region, so emptying the cart from in here
     swapped the body for the empty state and left the foot on screen with the
     old total under it -- the one thing in the drawer this function cannot
     re-render was also the one thing reading the store. A hard refresh fixed
     it, which is what proved it was the store and not the markup.

     syncStoreCounts() below is that extension. */
  function renderDrawerInPlace(res) {
    var html = res && res.sections && res.sections["section-ajax-cart"];
    if (typeof html !== "string" || !html) return false;
    var store = alpineCart();
    if (!store) return false;
    var body;
    try {
      body = new DOMParser().parseFromString(html, "text/html").querySelector("[data-cart-body]");
    } catch (e) {
      return false;
    }
    if (!body) return false;
    store.cartAjaxHtml = body.innerHTML;
    syncStoreCounts(store, body);
    return true;
  }

  /* ----- what the body's INNERHTML cannot carry -----

     renderDrawerInPlace hands the store `body.innerHTML`, so everything on
     the fresh [data-cart-body] element ITSELF is thrown away -- and that is
     where the section prints the numbers:

         data-items-count="{{ cart.item_count }}"
         data-cart-total="{{ cart.total_price }}"
         data-cart-total-price="{{ cart.total_price | money }}"

     The live element keeps whatever those said at page load. Copying them
     across is what makes `$store.cart.itemsCount` true, which is what hides
     the foot on an empty cart. The attributes are set on the live node too,
     for anything that reads the DOM rather than the store.

     DEFENSIVE ON PURPOSE. itemsCount is the theme's property, not ours -- it
     lives in the compiled bundle (assets/index-*.js) and nothing here can
     promise it is a plain writable field rather than a getter over something
     else. So: write it, then READ IT BACK, and only if the write did not take
     fall back to hiding the foot directly. The fallback is deliberately last:
     two mechanisms both setting style.display is how x-show bugs start, and
     this way the second one never runs unless the first is impossible. */
  function syncStoreCounts(store, freshBody) {
    var n = parseInt(freshBody.getAttribute("data-items-count"), 10);
    if (isNaN(n)) return;

    var live = qs("[data-cart-body]");
    if (live) {
      ["data-items-count", "data-cart-total", "data-cart-total-price"].forEach(function (name) {
        var v = freshBody.getAttribute(name);
        if (v !== null) live.setAttribute(name, v);
      });
    }

    var took = false;
    try {
      store.itemsCount = n;
      took = store.itemsCount === n;
    } catch (e) {
      took = false;
    }
    if (took) return;

    var foot = qs(".bb-cart-drawer__foot");
    if (foot) foot.style.display = n > 0 ? "" : "none";
  }

  /* Alpine re-binds the swapped-in markup through its own MutationObserver --
     the same way the drawer's x-html body gets its handlers. Nothing is called
     explicitly here on purpose: Alpine.initTree on a tree the observer has
     already initialised would bind every listener twice.

     Page first, then drawer: on /cart the drawer section is not rendered at
     all (layout/theme.liquid excludes it there), and in the drawer there is no
     .bb-cart-page, so exactly one of the two can ever match. refreshCart()
     stays as the last resort for a surface with neither. */
  function applyOrReload(res) {
    if (renderCartInPlace(res) || renderDrawerInPlace(res)) {
      setBusy(false);
      scheduleNavRechecks();
      return true;
    }
    refreshCart();
    return false;
  }

  /* ----- THE RENDER BRIDGE, for scripts outside this file -----

     assets/bb-prebuilt-qty.js hooks the prebuilt box stepper and Remove from
     outside, in the capture phase, because a prebuilt box's add-ons are tied
     to it only by _ub_grp and have to be scaled or removed alongside it. It
     then had no way to repaint, and said so:

         "A reload is blunt but it is the only refresh path that is certain to
          exist on both the cart page and the drawer -- bb-cart.js's own
          refresh is internal to it."

     True when it was written. The reload became a bug the moment the drawer
     learned to re-render in place, because a reload closes the drawer: a
     customer pressing + on a prebuilt box watched their cart vanish.

     refresh() is the answer, and it is deliberately ARGUMENT-FREE. The caller
     does not have to thread `sections` through its own fetches or hand us a
     response -- it just says "the cart changed, repaint" after its write has
     landed. That keeps the change at the other end down to two lines inside
     two functions, in a 22KB file holding the multi-address split, which is
     not a file to rewrite for this.

     It asks Shopify for the two sections directly (the Section Rendering API
     on the current URL), so it works the same on the cart page and in the
     drawer. Anything that goes wrong -- a failed fetch, a shape we cannot
     swap -- ends in the reload the caller would have done anyway, so the one
     outcome this can never produce is a page showing a stale cart. */
  window.BBCartRender = {
    SECTIONS: SECTIONS,

    /* For a caller that already has a sections-bearing response in hand. */
    render: function (res) {
      return applyOrReload(res);
    },

    refresh: function () {
      return fetch("/?sections=" + encodeURIComponent(SECTIONS), {
        headers: { Accept: "application/json" }
      })
        .then(function (r) { return r.json(); })
        .then(function (data) { applyOrReload({ sections: data }); })
        .catch(function () { window.location.reload(); });
    }
  };

  /* One mutation path: post with sections, render in place, undo the optimistic
     paint if the request fails.

     An `undo` means the caller is already showing its own in-flight state on the
     control that was clicked, so the cart-wide busy treatment is skipped --
     dimming the whole cart on every add-on click is the sluggishness we are
     removing. Callers with no local feedback (box qty, quick-view action) still
     get it. setBusy(false) afterwards either way: it is a no-op if unset. */
  function mutateCart(path, body, undo) {
    var local = typeof undo === "function";
    body.sections = SECTIONS;
    if (!local) setBusy(true);
    return postJson(path, body)
      .then(function (res) { applyOrReload(res); })
      .catch(function (err) {
        console.error("[BB cart]", err);
        if (local) undo();
        setBusy(false);
      });
  }

  function updateLines(updates) {
    return mutateCart("cart/update.js", { updates: updates });
  }

  function setGroupCopies(boxEl, copies) {
    var payload = parsePayload(boxEl);
    if (!payload || !payload.lines) return;
    copies = Math.max(1, copies | 0);
    var updates = {};
    payload.lines.forEach(function (line) {
      updates[line.key] = (Number(line.unitQty) || 1) * copies;
    });
    updateLines(updates);
  }

  function removeGroup(boxEl) {
    var payload = parsePayload(boxEl);
    if (!payload || !payload.lines) return;
    var updates = {};
    payload.lines.forEach(function (line) {
      updates[line.key] = 0;
    });
    updateLines(updates);
  }

  /* ONE PATH ON BOTH SURFACES, as of the drawer re-render.

     This used to hand the drawer case to the theme's own store.changeById(),
     on the reasoning that the drawer body IS the store's cartAjaxHtml so the
     store had to do the render. That was true before renderDrawerInPlace()
     existed. It is not any more, and the delegation had a cost the comment
     did not mention: store.changeById() reloads the document, and a reload
     shuts the drawer. Stepping a prebuilt box from 1 to 2 closed the cart and
     bounced the page -- the same fault removing a box used to have, left
     behind in the one handler that was not routed through mutateCart().

     Every quantity control now goes the same way: cart/update.js with
     sections, rendered in place. data-bb-box-qty-* (builder boxes) already
     did via setGroupCopies(); data-bb-line-* (prebuilt boxes and the per-row
     x in a box's contents) is what this fixes. */
  function changeLine(key, qty) {
    qty = Math.max(0, qty | 0);
    var updates = {};
    updates[key] = qty;
    return updateLines(updates);
  }

  function formatMoney(cents) {
    var format = (window.__bbCartCards && window.__bbCartCards.moneyFormat) || "${{amount}}";
    var value = (Number(cents) || 0) / 100;
    var amount = value.toFixed(2);
    return format.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, String(Math.round(value)));
  }

  function loadCardsConfig() {
    if (window.__bbCartCards) return window.__bbCartCards;
    var node = qs("[data-bb-cart-cards]");
    if (!node) return { products: [] };
    try {
      window.__bbCartCards = JSON.parse(node.textContent);
    } catch (e) {
      window.__bbCartCards = { products: [] };
    }
    return window.__bbCartCards;
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  /* ----- gift label -----
     Mirrors assets/box-builder.js. If you change the label format there,
     change it here too, or a box duplicated from the cart will disagree
     with one built in the builder. */
  var SMALL_WORDS = { and: 1, or: 1, of: 1, the: 1, a: 1, an: 1, to: 1, "for": 1 };
  var GIFT_NAME_MAX = 28;

  function capPart(part) {
    if (part.length < 2) return part;
    if (part === part.toLowerCase() || (part === part.toUpperCase() && part.length > 3)) {
      return part.charAt(0).toUpperCase() + part.slice(1).toLowerCase();
    }
    return part;
  }

  function titleCaseName(str) {
    var i = 0;
    return str.replace(/\S+/g, function (word) {
      var first = i++ === 0;
      if (!first && SMALL_WORDS[word.toLowerCase()]) return word.toLowerCase();
      if (word.length < 2) return word;
      return word.split(/([-'])/).map(function (part, idx) {
        return idx === 0 ? capPart(part.length < 2 ? part.toUpperCase() : part) : capPart(part);
      }).join("");
    });
  }

  function giftLabel(to, boxN) {
    var name = String(to || "").trim().replace(/\s+/g, " ");
    if (name && name.length <= GIFT_NAME_MAX) return titleCaseName(name) + "'s gift";
    return "Gift box " + boxN;
  }

  function fetchCart() {
    return fetch(cartRoot() + "cart.js", { credentials: "same-origin" })
      .then(function (res) { return res.json(); });
  }

  /* Box numbers are derived from the cart, never stored: the builder's own
     counter resets after every add, so anything that trusts it writes "1". */
  function boxOrder(cart) {
    var seen = [];
    (cart.items || []).forEach(function (item) {
      var id = item.properties && item.properties._bb_box_id;
      if (id && seen.indexOf(id) === -1) seen.push(id);
    });
    return seen;
  }

  function boxNumber(cart, boxId) {
    var pos = boxOrder(cart).indexOf(boxId);
    return pos === -1 ? 1 : pos + 1;
  }

  /* ----- scroll lock -----
     One pair for every modal on this page. `overflow: hidden` alone lands on
     <html>, which IS the scrolling element here, so it clamps scrollTop to 0
     and the position is gone before any restore can run — which is why saving
     and restoring scrollY on its own did nothing. Pinning the body with
     position:fixed and a negative top holds the view exactly where it was, and
     pinning its measured width keeps the horizontal box identical.

     Idempotent rather than refcounted: a second lock leaves the first saved
     position alone and an unlock always fully unlocks. The three modals here
     (card, quick view, prebuilt note) are mutually exclusive — every trigger
     for each one sits behind another's full-viewport backdrop, so none is
     reachable while another is open. If that ever stops being true the failure
     is a page that scrolls behind a modal, not a page that can never scroll
     again, which is the way round a refcount would get it wrong. */
  var scrollLocked = false;
  var scrollLockY = 0;
  var scrollLockDelta = 0;

  /* Width is PINNED to what body measured, never derived from
     innerWidth - clientWidth. Measured on this layout: body is 1778 wide both
     locked and unlocked, so the scrollbar gutter never became body width, and
     a padding-right of 15px removed space that was never lost — it shifted the
     centred .bb-cart sideways by 7px, half the gutter. Also note clientWidth
     reads 1793 while locked and lies; getBoundingClientRect is the only
     trustworthy width signal here. */
  function lockScroll() {
    if (scrollLocked) return;

    /* Read everything before writing anything. */
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    var bodyW = document.body.getBoundingClientRect().width;
    var docW = document.documentElement.getBoundingClientRect().width;
    scrollLockDelta = bodyW - docW;

    var s = document.body.style;
    s.position = "fixed";
    s.top = "-" + scrollLockY + "px";
    s.left = "0";
    /* No right: with left and an explicit width the box is already fully
       constrained, so right would be ignored. No paddingRight either. */
    s.width = bodyW + "px";
    document.documentElement.classList.add("bb-cart-modal-open");
    scrollLocked = true;
    window.addEventListener("resize", onLockedResize);
  }

  /* A pinned pixel width goes stale if the viewport changes while a modal is
     open -- phone rotation above all. body cannot be re-measured directly
     while it is fixed and pinned, so re-derive from the documentElement width
     plus the offset captured at lock time. */
  function onLockedResize() {
    if (!scrollLocked) return;
    document.body.style.width =
      document.documentElement.getBoundingClientRect().width + scrollLockDelta + "px";
  }

  function unlockScroll() {
    if (!scrollLocked) return;
    window.removeEventListener("resize", onLockedResize);
    var s = document.body.style;
    s.position = "";
    s.top = "";
    s.left = "";
    s.right = "";
    s.width = "";
    s.paddingRight = "";
    document.documentElement.classList.remove("bb-cart-modal-open");
    scrollLocked = false;
    window.scrollTo(0, scrollLockY);
  }

  /* The prebuilt-note modal is a separate controller in
     snippets/bb-cart-prebuilt-modal.liquid with its own IIFE, so it cannot
     reach these directly. Exposing the pair keeps one implementation instead
     of a second copy that drifts out of step. */
  window.BBScrollLock = { lock: lockScroll, unlock: unlockScroll };

  /* ----- modal ----- */
  function getModal() {
    return qs("body > [data-bb-cart-modal]") || qs(".main-cart [data-bb-cart-modal]") || qs("[data-bb-cart-modal]");
  }

  function cardProps(boxId, fields, boxN) {
    var n = boxN || 1;
    var props = {
      _bb_box_id: boxId,
      _bb_role: "card",
      _bb_box_n: String(n),
      _bb_unit_qty: "1",
      "Included in": giftLabel(fields.blank ? "" : fields.to, n)
    };
    if (fields.blank) {
      props._bb_card_blank = "true";
    } else {
      if (fields.to) props.To = String(fields.to).slice(0, 80);
      if (fields.from) props.From = String(fields.from).slice(0, 80);
      if (fields.message) props.Message = String(fields.message).slice(0, 300);
    }
    return props;
  }

  function readFields() {
    var modal = getModal();
    return {
      skip: !!(qs("[data-bb-cart-modal-skip]", modal) && qs("[data-bb-cart-modal-skip]", modal).checked),
      blank: !!(qs("[data-bb-cart-modal-blank]", modal) && qs("[data-bb-cart-modal-blank]", modal).checked),
      to: qs("[data-bb-cart-modal-to]", modal) ? qs("[data-bb-cart-modal-to]", modal).value.trim() : "",
      from: qs("[data-bb-cart-modal-from]", modal) ? qs("[data-bb-cart-modal-from]", modal).value.trim() : "",
      message: qs("[data-bb-cart-modal-message]", modal) ? qs("[data-bb-cart-modal-message]", modal).value.trim() : ""
    };
  }

  /* NOTHING IS HIDDEN AND NOTHING IS EXPLAINED.

     The fields used to be pulled out whenever no card was coming, which left
     a hole where they had been and then needed a sentence written to fill it.
     They stay put and fade. Two different fades, because the blank toggle has
     to stay clickable in one of them: is-inert kills the whole form (no card
     is coming, so there is nothing to leave blank either), is-blanked fades
     only [data-bb-cart-modal-fields] -- the writing -- which the toggle sits
     outside of, by design, so it can never fade itself out of reach.

     pointer-events alone would leave the inputs tabbable, hence `disabled`.
     Values survive that and are deliberately NOT cleared: editCard() returns
     before it reads to/from/message when fields.skip is true, and cardProps()
     ignores them when blank -- so switching designs, or bouncing off "No
     card" and back, costs the customer nothing they typed. */
  function syncFormVisibility() {
    var modal = getModal();
    var form = qs("[data-bb-cart-modal-form]", modal);
    var fieldsBox = qs("[data-bb-cart-modal-fields]", modal);
    var skip = qs("[data-bb-cart-modal-skip]", modal);
    var blank = qs("[data-bb-cart-modal-blank]", modal);
    var skipping = !!(skip && skip.checked);
    var isBlank = !!(blank && blank.checked);

    if (form) {
      form.hidden = false;
      form.classList.toggle("is-inert", skipping);
      form.classList.toggle("is-blanked", !skipping && isBlank);
    }
    if (blank) blank.disabled = skipping;
    if (fieldsBox) {
      qsa("input, textarea", fieldsBox).forEach(function (el) {
        el.disabled = skipping || isBlank;
      });
      /* Ticking blank, or picking the No card tile, ANSWERS the missing
         message -- so the prompt demanding one has to go with it, or it sits
         there in red over a set of faded fields nobody is being asked to
         fill in. */
      if (skipping || isBlank) fieldsBox.classList.remove("is-missing");
    }
  }

  /* THE SAVE-BLOCKED PROMPT, AT THE PICKER.

     It used to be a line in the footer beside Cancel -- the one spot in the
     dialog that cannot do anything about the problem -- so a refused Save
     read as a button that had broken rather than a step that was missing.
     The nudge on Save is now only a signal that something happened; what to
     do about it is said where the doing is. */
  function clearPrompt() {
    var modal = getModal();
    var pick = qs("[data-bb-cart-modal-pick]", modal);
    if (pick) pick.classList.remove("is-missing");
    var fieldsBox = qs("[data-bb-cart-modal-fields]", modal);
    if (fieldsBox) fieldsBox.classList.remove("is-missing");
  }

  function demandPick() {
    var pick = qs("[data-bb-cart-modal-pick]", getModal());
    if (!pick) return;
    pick.classList.add("is-missing");
    try { pick.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {}
  }

  /* THE SECOND BLOCKED STATE: a card chosen, blank not ticked, and no name in
     From. It used to focus the textarea and shake the Save button, which
     between them said nothing -- the red ring a focused field takes on is the
     ORDINARY focus style, so the only signal was the judder, and a judder
     does not tell anyone what is missing.

     WHY FROM AND NOT MESSAGE. The worst thing this shop can ship is a gift
     that arrives with no sender: the recipient cannot thank anyone and often
     cannot tell what it is. Requiring Message instead -- which is what this
     did -- allows exactly that card, a note signed by nobody. To matters
     least; the shipping label already carries the name.

     It is NOT conditional on having typed something. An empty card that saves
     anyway would be a second way to reach a blank card, next to the toggle --
     the two-controls-fighting problem the "No card" tile was built to remove.
     One way to each outcome: write something (From required), tick blank, or
     pick the No card tile.

     Same device as the picker, one level down: .is-missing on the fields
     wrapper reveals [data-bb-cart-modal-msg] under the name row and turns the
     From field's border red. It clears on the first keystroke, via the input
     listener at the bottom of this file, and on every open. */
  function demandFrom() {
    var modal = getModal();
    var fieldsBox = qs("[data-bb-cart-modal-fields]", modal);
    if (fieldsBox) fieldsBox.classList.add("is-missing");
    var input = qs("[data-bb-cart-modal-from]", modal);
    if (!input) return;
    try { input.focus({ preventScroll: true }); } catch (e) { try { input.focus(); } catch (e2) {} }
    try { input.scrollIntoView({ behavior: "smooth", block: "nearest" }); } catch (e) {}
  }

  /* Arrows are not decoration: if every design already fits, they do nothing,
     so they go. Measured rather than assumed -- the dialog width and the
     number of available cards both change. */
  function syncArrows() {
    var modal = getModal();
    var nav = qs("[data-bb-cart-modal-nav]", modal);
    var track = qs("[data-bb-cart-modal-track]", modal);
    if (nav && track) nav.hidden = track.scrollWidth <= track.clientWidth + 1;
  }

  /* THE "NO CARD" TILE.

     Sending a gift with no card used to be a link in the footer, which meant
     two controls answered the same question: the grid asked "which card?",
     the link asked "any card?". Now it is the first tile in the grid. One
     control, no mode.

     NONE_ID is a sentinel, not a variant: nothing is ever added to the cart
     for it. selectCard() turns it into `skip.checked = true` and every save
     path downstream -- readFields(), saveModal(), editCard(), duplicateBox(),
     cardProps() -- reads that checkbox exactly as it always did.

     THIS BLOCK WAS MISSING. renderCards() and selectCard() below have called
     noCardTile() and NONE_ID since 2026-10-06, but the definitions never
     landed in this file, so every open of the dialog -- Edit card, Add a
     card, Send to another recipient, on /cart and in the drawer -- threw
     "noCardTile is not defined" and the dialog never appeared. Found from the
     console on 2026-10-09. snippets/bb-cart-prebuilt-modal.liquid carries its
     own copy for the prebuilt dialog; keep the two in step. */
  var NONE_ID = "__bb_no_card__";

  var NO_CARD_SVG =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"' +
    ' stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="2.5" y="5" width="19" height="14" rx="2.5"/>' +
    '<path d="M3 7l9 6.5L21 7"/><path d="M3.5 20.5 20.5 3.5"/></svg>';

  /* Prepended in renderCards(). Move that unshift to a push and it sits at the
     END of the grid instead. Nothing else assumes a position. */
  function noCardTile(on) {
    return (
      '<article class="bb-cart-modal__card bb-cart-modal__card--none' +
        (on ? " is-selected" : "") + '"' +
        ' data-bb-modal-card data-variant-id="' + NONE_ID + '"' +
        ' role="button" tabindex="0"' +
        ' aria-label="Send this gift without a card' + (on ? ", selected" : "") + '">' +
        '<div class="bb-cart-modal__card-media">' + NO_CARD_SVG + "<em>No card</em>" +
          '<span class="bb-cart-modal__badge" aria-hidden="true">\u2713</span>' +
        "</div>" +
        "<h3>Send without a card</h3>" +
      "</article>"
    );
  }

  function renderCards(selectedId) {
    var modal = getModal();
    var track = qs("[data-bb-cart-modal-track]", modal);
    var empty = qs("[data-bb-cart-modal-empty]", modal);
    if (!track) return;
    var products = ((loadCardsConfig().products) || []).filter(function (product) {
      return product && product.available !== false && product.variantId;
    });
    if (!products.length) {
      track.innerHTML = "";
      if (empty) empty.hidden = false;
      return;
    }
    if (empty) empty.hidden = true;
    /* TWO CHANGES FROM THE BANNER VERSION, both deliberate.

       The badge is a CHIP now -- a 20px mark in the corner -- so its text is
       no longer the thing that says "selected"; the class on the tile is, and
       the CSS draws it. The label moves to aria-label so a screen reader is
       not left with a bare tick.

       The price renders only when the card COSTS something. "$0.00" under
       every tile is not a price, it is noise, and the cart row's card line
       follows the same rule. The day cards are priced this starts printing a
       real number with no markup change. */
    var tiles = products.map(function (product) {
      var selected = selectedId && String(product.variantId) === String(selectedId);
      var img = product.image
        ? '<img src="' + product.image + '" alt="' + escapeHtml(product.title) + '">'
        : "";
      return (
        '<article class="bb-cart-modal__card' + (selected ? " is-selected" : "") + '"' +
          ' data-bb-modal-card data-variant-id="' + product.variantId + '"' +
          ' role="button" tabindex="0"' +
          ' aria-label="' + escapeHtml(product.title) + (selected ? ", selected" : "") + '">' +
          '<div class="bb-cart-modal__card-media">' + img +
            '<span class="bb-cart-modal__badge" aria-hidden="true">✓</span>' +
          "</div>" +
          "<h3>" + escapeHtml(product.title) + "</h3>" +
          (product.price ? "<p>" + formatMoney(product.price) + "</p>" : "") +
        "</article>"
      );
    });

    /* Read from the checkbox, not from modalState: this runs on re-render too,
       and the checkbox is the thing that survives one. */
    var skipEl = qs("[data-bb-cart-modal-skip]", modal);
    tiles.unshift(noCardTile(!!(skipEl && skipEl.checked)));

    track.innerHTML = tiles.join("");
    syncArrows();
  }

  function cardByVariantId(id) {
    if (!id) return null;
    var hit = null;
    ((loadCardsConfig().products) || []).forEach(function (p) {
      if (String(p.variantId) === String(id)) hit = p;
    });
    return hit;
  }

  /* THE FOOTER NAMES A CARD OR SAYS NOTHING.

     "No card with this gift" restated a choice the customer had just made,
     one tile away. The tile is the statement; this only speaks when there is
     a card to name, and :empty hides the element entirely when it does not.
     The save-blocked prompt is not here either -- see demandPick(). */
  function syncChosen() {
    var modal = getModal();
    if (!modal) return;
    var skipEl = qs("[data-bb-cart-modal-skip]", modal);
    var skipping = !!(skipEl && skipEl.checked);
    var card = skipping ? null : cardByVariantId(modalState.selectedVariantId);
    var sum = qs("[data-bb-cart-modal-sum]", modal);
    if (!sum) return;
    /* NAME ONLY AT ZERO. "· Free" said nothing a customer did not already
       know -- every card is free today -- and it put a price-shaped thing in
       a dialog with no prices in it. The day cards carry a real figure this
       starts printing it, with no markup change. */
    sum.innerHTML = card
      ? "<strong>" + escapeHtml(card.title) + "</strong>" +
        (card.price ? " \u00b7 " + formatMoney(card.price) : "")
      : "";
  }

  function selectCard(variantId) {
    var modal = getModal();
    var none = String(variantId) === NONE_ID;

    modalState.selectedVariantId = none ? null : variantId;
    modalState.skip = none;
    var skip = qs("[data-bb-cart-modal-skip]", modal);
    if (skip) skip.checked = none;

    qsa("[data-bb-modal-card]", modal).forEach(function (tile) {
      var on = String(tile.getAttribute("data-variant-id")) === String(variantId);
      tile.classList.toggle("is-selected", on);
      /* The chip is drawn by the is-selected class; nothing to write here. */
    });
    /* Picking anything answers the question the prompt was asking. */
    clearPrompt();
    syncChosen();
    syncFormVisibility();
  }

  function openModal(opts) {
    var modal = getModal();
    if (!modal) return;
    if (modal.parentNode !== document.body) document.body.appendChild(modal);
    opts = opts || {};
    modalState.mode = opts.mode || "edit";
    modalState.boxId = opts.boxId || "";
    modalState.payload = opts.payload || null;
    modalState.selectedVariantId = opts.cardVariantId || (opts.payload && opts.payload.cardVariantId) || null;
    /* FALSE, not `!selectedVariantId`. A box with no card used to open with
       skip already ticked, which now means the "No card" tile would be
       pre-chosen on the one screen whose job is to get a card chosen. Opening
       with nothing selected hands the decision back: Save refuses until the
       customer picks a tile, and the footer tells them so.

       The cost is one extra click when duplicating a box that never had a
       card. That is the right trade -- it is an explicit choice on a new
       gift, not a default inherited from the old one. */
    modalState.skip = false;

    var duplicate = modalState.mode === "duplicate";
    var title = qs("[data-bb-cart-modal-title]", modal);
    var lede = qs("[data-bb-cart-modal-lede]", modal);
    var save = qs("[data-bb-cart-modal-save]", modal);
    var toLabel = qs("[data-bb-cart-modal-to-label]", modal);
    var toInput = qs("[data-bb-cart-modal-to]", modal);
    /* ADDING AND EDITING ARE NOT THE SAME SCREEN. "Update the card included
       with this gift" on a box that has no card yet describes something that
       does not exist. cardVariantId is the test -- snippets/bb-cart-box.liquid
       sets it from the real card LINE, so it is null exactly when there is no
       card to update. */
    var hasCard = !!modalState.selectedVariantId;
    if (title) {
      title.textContent = duplicate
        ? "Send this gift to someone else"
        : (hasCard
            ? (opts.to ? "Edit " + opts.to + "'s card" : "Edit card")
            : "Add a card");
    }
    if (lede) {
      lede.textContent = duplicate
        ? "We’ll add another to your cart. Change who it’s for, and the message if you need to."
        : (hasCard
            ? "Update the card included with this gift."
            : "Choose a design and write your message.");
    }
    if (save) save.textContent = duplicate ? "Add this gift" : "Save Changes";
    /* "To (new recipient)" named a field rather than asked a question, and the
       label is doing nothing the placeholder cannot. */
    if (toLabel) toLabel.textContent = "To";
    if (toInput) toInput.placeholder = duplicate ? "Who’s this one for?" : "Recipient’s name";

    var to = qs("[data-bb-cart-modal-to]", modal);
    var from = qs("[data-bb-cart-modal-from]", modal);
    var message = qs("[data-bb-cart-modal-message]", modal);
    var blank = qs("[data-bb-cart-modal-blank]", modal);
    var skip = qs("[data-bb-cart-modal-skip]", modal);
    var count = qs("[data-bb-cart-modal-count]", modal);
    /* THE MESSAGE CARRIES OVER ON A DUPLICATE; ONLY "To" CLEARS.

       Both shapes of customer land on this screen. A corporate order sends one
       message to twenty people and changes nothing but the name, so clearing
       the message made them retype it twenty times. A personal sender usually
       does rewrite it -- but the message is sitting in front of them in an
       editable field, and To is empty and focused, so the thing that has to
       change is the thing their cursor is already in.

       Blank carries for the same reason: a duplicate of a deliberately blank
       card should be blank too. */
    if (to) to.value = duplicate ? "" : (opts.to || "");
    if (from) from.value = opts.from || "";
    if (message) message.value = opts.message || "";
    if (blank) blank.checked = (opts.blank === true || opts.blank === "true");
    if (skip) skip.checked = modalState.skip;
    if (count) count.textContent = String((message && message.value || "").length);

    renderCards(modalState.selectedVariantId);
    if (modalState.selectedVariantId) selectCard(modalState.selectedVariantId);
    else syncFormVisibility();

    clearPrompt();
    syncChosen();

    modal.hidden = false;
    lockScroll();

    /* AFTER the dialog is on screen, not before. renderCards() measures the
       track to decide whether the arrows have anything to scroll, and while
       the modal is still [hidden] every box it measures is 0 wide -- so the
       nav hid itself on every open and stayed hidden. */
    syncArrows();

    /* The one field a duplicate must change is the one the cursor lands in. */
    if (duplicate && toInput && !modalState.skip) {
      try { toInput.focus(); } catch (e) {}
    }
  }

  function closeModal() {
    var modal = getModal();
    if (!modal) return;
    modal.hidden = true;
    unlockScroll();
    modalState.payload = null;
  }

  /* THERE IS NO shakeSave() ANY MORE, and nothing should bring it back.

     It added .is-shake to the Save button for 420ms. The complaint was that
     it read as the site glitching rather than as a refusal, which is what a
     judder always risks when the thing it is attached to has not said
     anything. Both blocked states now put a message beside the control that
     is actually missing something -- demandPick() at the strip, demandFrom()
     under the name row -- so the judder was covering for copy that now
     exists. The @keyframes went with it from assets/bb-cart.css.

     Keep snippets/bb-cart-prebuilt-modal.liquid in step: it had its own
     copy of this function for its own Save button. */
  function saveModal() {
    var fields = readFields();
    var payload = modalState.payload;
    if (!payload) return closeModal();

    if (!fields.skip) {
      if (!modalState.selectedVariantId) {
        demandPick();
        return;
      }
      if (!fields.blank && !fields.from) {
        demandFrom();
        return;
      }
    }

    if (modalState.mode === "duplicate") {
      duplicateBox(payload, fields);
    } else {
      editCard(payload, fields);
    }
  }

  /* ----- "Included in" sweep -----
     The label is written ONCE per line, when the line is added, so renaming the
     recipient on a saved card used to update the CARD line only: every other
     line in the box kept the old name and one box reported two recipients to
     Shopify admin, to the multi-address app and to Extensiv.

     /cart/update.js cannot touch line item properties -- it changes quantities
     only -- so each line goes through /cart/change.js on its own key, carrying
     its COMPLETE existing properties object with just this one key replaced.
     Anything dropped here is dropped from the order: the duplicate path made
     exactly that mistake with _bb_source, Occasion and this very label.

     SEQUENTIAL on purpose. /cart/change.js re-keys the line it touches, so two
     in flight against one cart race and the later reply overwrites the earlier
     line's work. Chained one at a time, and the line list is re-read from
     /cart.js at the moment of the sweep because the card save has already
     re-keyed the card line.

     The card line is skipped: cardProps already wrote the new label on it.

     Never rejects and never renders per line. It resolves with the freshest
     /cart/change.js response that carried sections, or null if it changed
     nothing, and the CALLER does the single render. A line that fails is
     logged and the rest still run -- a partly swept box is no worse than the
     unswept box we started from, and the card edit is committed either way. */
  function sweepBoxLabels(boxId, label) {
    if (!boxId || !label) return Promise.resolve(null);
    return fetchCart().then(function (cart) {
      var stale = (cart.items || []).filter(function (item) {
        var props = item.properties || {};
        if (!item.key) return false;
        if (props._bb_box_id !== boxId) return false;
        if (props._bb_role === "card") return false;
        return props["Included in"] !== label;
      });
      var latest = null;
      return stale.reduce(function (chain, item) {
        return chain.then(function () {
          /* Verbatim copy first, one key overwritten after. */
          var props = {};
          var existing = item.properties || {};
          Object.keys(existing).forEach(function (k) { props[k] = existing[k]; });
          props["Included in"] = label;
          return postJson("cart/change.js", {
            id: item.key,
            quantity: item.quantity,
            properties: props,
            sections: SECTIONS
          }).then(function (res) {
            latest = res;
          }).catch(function (err) {
            console.error("[BB cart] could not relabel line " + item.key, err);
          });
        });
      }, Promise.resolve()).then(function () { return latest; });
    }).catch(function (err) {
      console.error("[BB cart] label sweep skipped", err);
      return null;
    });
  }

  function editCard(payload, fields) {
    var copies = Math.max(1, Number(payload.copies) || 1);
    var cardKey = payload.cardKey;
    var currentVariant = payload.cardVariantId;

    if (fields.skip) {
      if (!cardKey) {
        closeModal();
        return;
      }
      var updates = {};
      updates[cardKey] = 0;
      closeModal();
      return updateLines(updates);
    }

    var qty = copies;

    closeModal();
    setBusy(true);

    /* Read the cart first: the replacement card must carry this box's real
       position, not a hardcoded "1". */
    return fetchCart().then(function (cart) {
      var props = cardProps(payload.boxId, fields, boxNumber(cart, payload.boxId));
      var label = props["Included in"];

      /* Decided from the PRE-save cart, which still holds the old labels, so an
         edit that only changes the message costs nothing extra: no /cart.js
         re-read and no change.js calls, just the card save that was always
         there. Every non-card line is tested rather than one sample line, so a
         box already carrying two different labels gets healed instead of
         half-checked. */
      var needsSweep = (cart.items || []).some(function (item) {
        var p = item.properties || {};
        if (p._bb_box_id !== payload.boxId) return false;
        if (p._bb_role === "card") return false;
        return p["Included in"] !== label;
      });

      /* ONE render, after the sweep -- not one per line. The card save is the
         part that must never be lost, so its own response is the fallback: a
         sweep that finds nothing, bails, or fails on every line still paints
         the saved card. */
      function finish(res) {
        if (!needsSweep) {
          applyOrReload(res);
          return;
        }
        return sweepBoxLabels(payload.boxId, label).then(function (swept) {
          applyOrReload(swept || res);
        });
      }

      if (cardKey && String(currentVariant) === String(modalState.selectedVariantId)) {
        return postJson("cart/change.js", {
          id: cardKey,
          quantity: qty,
          properties: props,
          sections: SECTIONS
        }).then(finish);
      }

      return postJson("cart/add.js", {
        items: [{ id: modalState.selectedVariantId, quantity: qty, properties: props }],
        sections: SECTIONS
      }).then(function (addRes) {
        if (!cardKey) return finish(addRes);
        var remove = {};
        remove[cardKey] = 0;
        return postJson("cart/update.js", { updates: remove, sections: SECTIONS }).then(finish);
      });
    }).catch(function (err) {
      console.error("[BB cart]", err);
      setBusy(false);
    });
  }

  function duplicateBox(payload, fields) {
    /* Nothing to copy and no card chosen -- there is no duplicate to make.
       It used to shake Save, which said only that the button had refused.
       The strip is the one control that can get out of this state. */
    if (!(payload.lines || []).length && !modalState.selectedVariantId) {
      demandPick();
      return;
    }
    closeModal();
    setBusy(true);

    /* Copy each line's ACTUAL properties from the cart rather than rebuilding
       four of them from the payload. The old version silently dropped
       Occasion, _bb_source, the packing list and the "Included in" label, so a
       duplicated box reached the warehouse half-labelled. */
    return fetchCart().then(function (cart) {
      var newId = "bb-" + Date.now() + "-" + Math.floor(Math.random() * 1000);
      var newN = boxOrder(cart).length + 1;
      var label = giftLabel(fields.skip || fields.blank ? "" : fields.to, newN);
      var items = [];

      (cart.items || []).forEach(function (item) {
        var props = item.properties || {};
        if (props._bb_box_id !== payload.boxId) return;
        if (props._bb_role === "card") return;
        if (!item.variant_id) return;
        var next = {};
        Object.keys(props).forEach(function (k) { next[k] = props[k]; });
        /* Only the identity of the box changes. */
        next._bb_box_id = newId;
        next._bb_box_n = String(newN);
        next["Included in"] = label;
        items.push({
          id: item.variant_id,
          /* One copy, not however many the original had. */
          quantity: Number(props._bb_unit_qty) || 1,
          properties: next
        });
      });

      if (!fields.skip && modalState.selectedVariantId) {
        items.push({
          id: modalState.selectedVariantId,
          quantity: 1,
          properties: cardProps(newId, fields, newN)
        });
      }

      if (!items.length) {
        setBusy(false);
        return;
      }

      return postJson("cart/add.js", { items: items, sections: SECTIONS })
        .then(function (res) { applyOrReload(res); });
    }).catch(function (err) {
      console.error("[BB cart]", err);
      setBusy(false);
    });
  }

  function bindModal() {
    document.addEventListener("click", function (event) {
      if (!event.target.closest("[data-bb-cart-modal]")) return;
      if (event.target.closest("[data-bb-cart-modal-close]")) {
        event.preventDefault();
        closeModal();
        return;
      }
      if (event.target.closest("[data-bb-cart-modal-save]")) {
        event.preventDefault();
        saveModal();
        return;
      }
      var tile = event.target.closest("[data-bb-modal-card]");
      if (tile) {
        event.preventDefault();
        selectCard(tile.getAttribute("data-variant-id"));
        /* THE SECOND RED RING. These tiles are tabindex elements rather than
           real buttons, and Chrome hands those :focus-visible on a plain
           mouse click -- so a clicked card got the selected border AND the
           focus outline, two reds around the same thing. Dropping focus here
           leaves the keyboard path untouched: Enter and Space are handled in
           the keydown listener below and keep the ring, which is the only
           place it is actually needed. */
        if (tile.blur) tile.blur();
        return;
      }
      var modal = getModal();
      if (event.target.closest("[data-bb-cart-cards-prev]")) {
        var track = qs("[data-bb-cart-modal-track]", modal);
        if (track) track.scrollBy({ left: -track.clientWidth * 0.8, behavior: "smooth" });
      }
      if (event.target.closest("[data-bb-cart-cards-next]")) {
        var trackNext = qs("[data-bb-cart-modal-track]", modal);
        if (trackNext) trackNext.scrollBy({ left: trackNext.clientWidth * 0.8, behavior: "smooth" });
      }
    });
    document.addEventListener("change", function (event) {
      var modal = event.target.closest("[data-bb-cart-modal]");
      if (!modal) return;
      if (event.target.matches("[data-bb-cart-modal-skip]")) {
        modalState.skip = !!event.target.checked;
        if (event.target.checked) {
          qsa("[data-bb-modal-card]", modal).forEach(function (tile) {
            tile.classList.remove("is-selected");
          });
        }
        syncChosen();
        syncFormVisibility();
      }
      if (event.target.matches("[data-bb-cart-modal-blank]")) syncFormVisibility();
    });
    /* DRAG TO SCROLL. Touch already does this natively; this is for a mouse,
       which otherwise has only shift+wheel and the arrows. The 4px threshold
       is what keeps a drag from also counting as a click on whichever card
       the gesture happened to start on. */
    (function () {
      var down = false, moved = false, startX = 0, startLeft = 0, el = null;

      document.addEventListener("pointerdown", function (event) {
        if (event.button !== 0) return;
        var t = event.target.closest("[data-bb-cart-modal-track]");
        if (!t) return;
        el = t; down = true; moved = false;
        startX = event.clientX; startLeft = t.scrollLeft;
      });

      window.addEventListener("pointermove", function (event) {
        if (!down || !el) return;
        var dx = event.clientX - startX;
        if (!moved && Math.abs(dx) < 4) return;
        if (!moved) { moved = true; el.classList.add("is-dragging"); }
        event.preventDefault();
        el.scrollLeft = startLeft - dx;
      }, { passive: false });

      window.addEventListener("pointerup", function () {
        if (!down) return;
        down = false;
        var t = el; el = null;
        if (moved && t) window.setTimeout(function () { t.classList.remove("is-dragging"); }, 0);
      });

      window.addEventListener("resize", syncArrows);
    })();

    document.addEventListener("input", function (event) {
      var modal = event.target.closest("[data-bb-cart-modal]");
      if (!modal) return;

      if (event.target.matches("[data-bb-cart-modal-message]")) {
        var count = qs("[data-bb-cart-modal-count]", modal);
        if (count) count.textContent = String(event.target.value.length);
        return;
      }

      /* The prompt clears on the first keystroke in the field it is about,
         not on Save and not on any keystroke anywhere. Scoped to this modal's
         own fields wrapper rather than clearPrompt(), which also clears the
         picker -- typing a name says nothing about whether a design has been
         chosen. */
      if (event.target.matches("[data-bb-cart-modal-from]")) {
        var fieldsBox = qs("[data-bb-cart-modal-fields]", modal);
        if (fieldsBox) fieldsBox.classList.remove("is-missing");
      }
    });
    document.addEventListener("keydown", function (event) {
      var modal = getModal();
      if (event.key === "Escape" && modal && !modal.hidden) closeModal();

      /* The tiles carry role="button" and tabindex="0", so they have to answer
         Enter and Space the way a button would. This is the only keyboard
         route to "no card" now that the footer checkbox is display:none. */
      if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") return;
      if (!event.target.closest("[data-bb-cart-modal]")) return;
      var tile = event.target.closest("[data-bb-modal-card]");
      if (!tile) return;
      event.preventDefault();
      selectCard(tile.getAttribute("data-variant-id"));
    });
  }

  function triggerFromButton(btn) {
    var boxEl = btn.closest("[data-bb-cart-box]");
    openModal({
      mode: btn.getAttribute("data-bb-mode") || "edit",
      boxId: btn.getAttribute("data-bb-box-id"),
      payload: boxEl ? parsePayload(boxEl) : null,
      to: btn.getAttribute("data-bb-to"),
      from: btn.getAttribute("data-bb-from"),
      message: btn.getAttribute("data-bb-message"),
      cardTitle: btn.getAttribute("data-bb-card-title"),
      blank: btn.getAttribute("data-bb-blank"),
      cardVariantId: boxEl && parsePayload(boxEl) && parsePayload(boxEl).cardVariantId
    });
  }

  document.addEventListener("click", function (event) {
    var toggle = event.target.closest("[data-bb-box-toggle]");
    if (toggle) {
      var box = toggle.closest("[data-bb-cart-box]");
      if (!box) return;
      box.classList.toggle("is-open");
      toggle.setAttribute("aria-expanded", box.classList.contains("is-open") ? "true" : "false");
      return;
    }

    var dec = event.target.closest("[data-bb-box-qty-dec]");
    var inc = event.target.closest("[data-bb-box-qty-inc]");
    if (dec || inc) {
      event.preventDefault();
      var boxEl = event.target.closest("[data-bb-cart-box]");
      if (!boxEl) return;
      var copies = parseInt(boxEl.getAttribute("data-bb-copies"), 10) || 1;
      setGroupCopies(boxEl, copies + (inc ? 1 : -1));
      return;
    }

    var remove = event.target.closest("[data-bb-box-remove]");
    if (remove) {
      event.preventDefault();
      var group = remove.closest("[data-bb-cart-box]");
      if (group) removeGroup(group);
      return;
    }

    var lineDec = event.target.closest("[data-bb-line-dec]");
    var lineInc = event.target.closest("[data-bb-line-inc]");
    if (lineDec || lineInc) {
      event.preventDefault();
      var wrap = event.target.closest("[data-bb-line-qty]");
      var line = event.target.closest("[data-bb-cart-line]");
      var key = (wrap && wrap.getAttribute("data-key")) || (line && line.getAttribute("data-key"));
      if (!key) return;
      var valEl = wrap && wrap.querySelector(".bb-qty__val");
      var qty = parseInt(valEl && valEl.textContent, 10) || 1;
      changeLine(key, qty + (lineInc ? 1 : -1));
      return;
    }

    var lineRemove = event.target.closest("[data-bb-line-remove]");
    if (lineRemove) {
      event.preventDefault();
      changeLine(lineRemove.getAttribute("data-key"), 0);
      return;
    }

    var editCardBtn = event.target.closest("[data-bb-cart-edit-card], [data-bb-cart-duplicate]");
    if (editCardBtn) {
      event.preventDefault();
      triggerFromButton(editCardBtn);
      return;
    }

    var editBox = event.target.closest("[data-bb-box-edit]");
    if (editBox) {
      event.preventDefault();
      event.stopPropagation();
      var boxEl = editBox.closest("[data-bb-cart-box]");
      if (boxEl) beginEditBox(boxEl, editBox.getAttribute("href"));
    }
  });

  var MS_LEAVE_KEY = "bb_ms_leaving";

  function isMsPage() {
    return location.pathname.indexOf("/a/ms/cart") !== -1;
  }

  function clearMsLeaving() {
    try { sessionStorage.removeItem(MS_LEAVE_KEY); } catch (e) {}
    document.documentElement.removeAttribute("data-bb-ms-leaving");
    var box = document.getElementById("ms__toggle-box");
    if (box) box.classList.remove("is-busy");
    document.querySelectorAll(".ub-ms-loader").forEach(function (el) {
      if (el.parentNode) el.parentNode.removeChild(el);
    });
  }

  function markMsLeaving() {
    try { sessionStorage.setItem(MS_LEAVE_KEY, "1"); } catch (e) {}
    document.documentElement.setAttribute("data-bb-ms-leaving", "1");
    var box = document.getElementById("ms__toggle-box");
    if (box) box.classList.add("is-busy");
  }

  function ensureMsLoader(msg) {
    var ov = document.querySelector(".ub-ms-loader");
    if (!ov) {
      ov = document.createElement("div");
      ov.className = "ub-ms-loader";
      ov.setAttribute("role", "alert");
      ov.setAttribute("aria-live", "assertive");
      ov.setAttribute("aria-busy", "true");
      ov.innerHTML = '<div class="ub-spin"></div><div class="ub-load-txt"></div>';
      document.body.appendChild(ov);
    }
    var txt = ov.querySelector(".ub-load-txt");
    if (txt) txt.textContent = msg || "Taking you to multiple addresses…";
    requestAnimationFrame(function () {
      ov.classList.add("ub-on");
    });
    return ov;
  }

  function showMsLoader(msg) {
    markMsLeaving();
    ensureMsLoader(msg);
    /* Keep overlay until /a/ms/cart navigation; recreate if cart section re-renders wipe it. */
    if (!window.__bbMsLeaveWatch) {
      window.__bbMsLeaveWatch = true;
      var observer = new MutationObserver(function () {
        if (document.documentElement.getAttribute("data-bb-ms-leaving") !== "1") return;
        if (isMsPage()) {
          clearMsLeaving();
          return;
        }
        if (!document.querySelector(".ub-ms-loader")) ensureMsLoader(msg);
        var box = document.getElementById("ms__toggle-box");
        if (box) box.classList.add("is-busy");
      });
      observer.observe(document.documentElement, { childList: true, subtree: true });
      setTimeout(function () {
        if (!isMsPage() && document.documentElement.getAttribute("data-bb-ms-leaving") === "1") {
          clearMsLeaving();
        }
      }, 90000);
    }
  }

  /* Restore overlay after a cart re-render / soft reload mid-redirect */
  try {
    if (!isMsPage() && sessionStorage.getItem(MS_LEAVE_KEY) === "1") {
      showMsLoader("Taking you to multiple addresses…");
    }
  } catch (e) {}

  document.addEventListener("click", function (event) {
    var multi = event.target.closest("#ms__toggle-box, .bb-cart-summary__multi-btn, #ms__toggle-shipping");
    if (!multi) return;
    if (document.documentElement.getAttribute("data-bb-ms-leaving") === "1") {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    showMsLoader("Taking you to multiple addresses…");
  }, true);

  /* ----- cart-side add-ons -----
     TWO SHAPES, ONE PATH.

     kind "bb" (builder box): same box id, same role as a builder-chosen item,
     so it renders inside the box's item list with no template work and groups
     correctly on the packing slip. "Included in" is copied from a sibling line
     so the new item carries the same label as the rest of the box.

     kind "ub" (prebuilt box): Bundle / _box_sku / _ub_grp / Gift to — exactly
     what snippets/ub-extras-add.liquid writes for a PDP extra, so the packing
     slip and Extensiv need no change at all. NEVER mix the two vocabularies.

     addAddonToBox is the ONE add path: the strip button and the quick-view
     button both call it, so there is a single fetch to keep correct. */
  /* ----- optimistic tile state -----
     Flip the tile the instant it is clicked so the strip feels immediate, then
     let the server HTML land on top of it. The optimistic paint only has to
     survive until the swap replaces this node, so there is nothing to reconcile
     on success; the returned undo puts it back when the request fails.

     The markup mirrors bb-cart-addons.liquid exactly -- both labels, because
     CSS picks which of the two shows on hover. If the labels change there,
     change them here. */
  function optimisticTile(btn, adding) {
    if (!btn) return null;
    var tile = btn.closest(".bb-addon");
    var prevHtml = btn.innerHTML;
    var prevLabel = btn.getAttribute("aria-label");
    var prevAdded = tile ? tile.classList.contains("is-added") : false;

    if (tile) tile.classList.toggle("is-added", !!adding);
    btn.innerHTML = adding
      ? '<span class="bb-addon__lbl-added">✓ Added</span><span class="bb-addon__lbl-remove">Remove</span>'
      : "Add";
    /* Disabled for the in-flight window so a double click cannot post twice. */
    btn.disabled = true;
    btn.classList.add("is-busy");

    return function undo() {
      btn.innerHTML = prevHtml;
      if (prevLabel !== null) btn.setAttribute("aria-label", prevLabel);
      if (tile) tile.classList.toggle("is-added", prevAdded);
      btn.disabled = false;
      btn.classList.remove("is-busy");
    };
  }

  /* Every hook the strip puts on a button, in one place. Read from the tile
     button and from the quick-view action button, which is a copy of it. */
  function addonOptsFromButton(btn) {
    return {
      kind: btn.getAttribute("data-bb-addon-kind") || "bb",
      boxId: btn.getAttribute("data-bb-addon-box") || "",
      variantId: btn.getAttribute("data-bb-addon-variant") || "",
      label: btn.getAttribute("data-bb-addon-label") || "",
      grp: btn.getAttribute("data-bb-addon-grp") || "",
      bundle: btn.getAttribute("data-bb-addon-bundle") || "",
      boxSku: btn.getAttribute("data-bb-addon-boxsku") || "",
      giftTo: btn.getAttribute("data-bb-addon-giftto") || "",
      boxKey: btn.getAttribute("data-bb-addon-boxkey") || ""
    };
  }

  /* Same alphabet and length as the mint in ub-extras-add.liquid and the
     duplicate path in bb-cart-prebuilt-modal.liquid. Do NOT try to tell a
     _ub_grp from a _bb_box_id by shape — both are 'g' + base36. */
  function mintGrp() {
    return "g" + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
  }

  /* A prebuilt add-on carries EXACTLY the properties a PDP extra carries
     (snippets/ub-extras-add.liquid, verified on order #106293), and nothing
     else. No _bb_* keys: those would make bb-cart-list.liquid draw a phantom
     builder card above the prebuilt one and would hand Extensiv a vocabulary
     that has never appeared in a real order. */
  function ubAddonProps(opts, grp) {
    var props = {
      Bundle: opts.bundle || "",
      _box_sku: opts.boxSku || ""
    };
    if (grp) props._ub_grp = grp;
    if (opts.giftTo) props["Gift to"] = opts.giftTo;
    return props;
  }

  /* Not every prebuilt box has a _ub_grp -- order #106292 has two with none --
     so one is minted on the FIRST Add click and written onto the box line.

     The box's existing properties are copied VERBATIM out of the DOM and sent
     straight back with the new key added. _bundle_note is never parsed and
     never rebuilt: it is the pack list the warehouse ships from, and the modal
     already refuses to save when it cannot round-trip it. The one check made
     here is that the string still parses as JSON -- that only proves the
     attribute round-trip did not corrupt it, and the value posted is the
     original string either way.

     Rejects rather than throws so the caller can fall back to adding the item
     untagged. A failed tag must NEVER block the add. */
  function tagPrebuiltBox(boxKey, grp) {
    var boxEl = null;
    var boxes = qsa("[data-bb-prebuilt][data-bb-ub-props]");
    for (var i = 0; i < boxes.length; i++) {
      if (boxes[i].getAttribute("data-key") === boxKey) {
        boxEl = boxes[i];
        break;
      }
    }
    if (!boxEl) return Promise.reject(new Error("no box line for key " + boxKey));

    var existing;
    try {
      existing = JSON.parse(boxEl.getAttribute("data-bb-ub-props") || "");
    } catch (e) {
      return Promise.reject(e);
    }
    if (!existing || typeof existing !== "object" ||
        Object.prototype.toString.call(existing) === "[object Array]") {
      return Promise.reject(new Error("box properties are not an object"));
    }
    if (typeof existing._bundle_note === "string" && existing._bundle_note) {
      try {
        JSON.parse(existing._bundle_note);
      } catch (e2) {
        return Promise.reject(new Error("_bundle_note did not survive the DOM round-trip"));
      }
    }

    var props = {};
    Object.keys(existing).forEach(function (k) { props[k] = existing[k]; });
    props._ub_grp = grp;

    var qty = parseInt(boxEl.getAttribute("data-bb-qty"), 10);
    return postJson("cart/change.js", {
      id: boxKey,
      quantity: qty > 0 ? qty : 1,
      properties: props
    });
  }

  function addUbAddon(opts, undo) {
    function send(useGrp) {
      return mutateCart(
        "cart/add.js",
        {
          items: [{
            id: opts.variantId,
            quantity: 1,
            properties: ubAddonProps(opts, useGrp)
          }]
        },
        undo
      );
    }

    if (opts.grp) return send(opts.grp);
    if (!opts.boxKey) return send("");

    var minted = mintGrp();
    return tagPrebuiltBox(opts.boxKey, minted).then(
      function () { return send(minted); },
      function (err) {
        /* The warehouse still groups this by Bundle; it just will not show as
           "✓ Added" in the strip until the box is tagged. */
        console.error("[BB cart] could not tag prebuilt box with a group id", err);
        return send("");
      }
    );
  }

  /* THE one add path: the strip button and the quick-view button both call it,
     so there is a single fetch to keep correct. Takes an options object built
     by addonOptsFromButton and branches on kind. */
  function addAddonToBox(opts) {
    if (!opts || !opts.variantId) return;
    var undo = optimisticTile(opts.btn || null, true);

    if (opts.kind === "ub") return addUbAddon(opts, undo);

    if (!opts.boxId) {
      if (undo) undo();
      return;
    }
    var props = {
      _bb_box_id: opts.boxId,
      _bb_role: "goodies",
      _bb_source: "cart-addon",
      _bb_unit_qty: "1"
    };
    if (opts.label) props["Included in"] = opts.label;
    return mutateCart(
      "cart/add.js",
      { items: [{ id: opts.variantId, quantity: 1, properties: props }] },
      undo
    );
  }

  /* Undo goes through the same in-place path as the add, not store.changeById,
     which is what reloaded the page. */
  function removeAddonLine(key, btn) {
    if (!key) return;
    var updates = {};
    updates[key] = 0;
    return mutateCart("cart/update.js", { updates: updates }, optimisticTile(btn, false));
  }

  function addToBox(btn) {
    if (btn.disabled) return;
    var opts = addonOptsFromButton(btn);
    opts.btn = btn;
    addAddonToBox(opts);
  }

  /* ----- add-on quick view -----
     Built once on demand and reused. The tile already carries the title,
     price, description and image URLs as data attributes, and the box/variant
     live on its own button, so nothing extra is rendered per box. */
  var qvEl = null;
  var qvOpener = null;

  /* Focusing scrolls the target into view by default, which yanked the page to
     the top when the dialog opened. preventScroll is ignored, not thrown, on
     old browsers; the catch is belt and braces. */
  function focusNoScroll(el) {
    if (!el || typeof el.focus !== "function") return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  function buildQuickView() {
    if (qvEl) {
      /* Re-attach if anything detached it; appendChild on a live node is a no-op move. */
      if (qvEl.parentNode !== document.body) document.body.appendChild(qvEl);
      return qvEl;
    }
    qvEl = document.createElement("div");
    qvEl.className = "bb-qv";
    qvEl.hidden = true;
    /* Three rows: a fixed head holding the close, a scrolling body, and a
       pinned foot holding the action. The action must stay reachable without
       scrolling, so the PANEL never scrolls -- only .bb-qv__body does. */
    qvEl.innerHTML =
      '<button type="button" class="bb-qv__scrim" data-bb-qv-close aria-label="Close"></button>' +
      '<div class="bb-qv__panel" role="dialog" aria-modal="true" aria-labelledby="bb-qv-title">' +
        '<div class="bb-qv__head">' +
          '<button type="button" class="bb-qv__close" data-bb-qv-close aria-label="Close">&times;</button>' +
        "</div>" +
        '<div class="bb-qv__body" data-bb-qv-body>' +
          '<img class="bb-qv__img" alt="" data-bb-qv-img>' +
          '<div class="bb-qv__thumbs" data-bb-qv-thumbs hidden></div>' +
          '<h2 class="bb-qv__title" id="bb-qv-title" data-bb-qv-title></h2>' +
          '<p class="bb-qv__price" data-bb-qv-price></p>' +
          '<p class="bb-qv__desc" data-bb-qv-desc></p>' +
        "</div>" +
        '<div class="bb-qv__foot">' +
          '<button type="button" class="bb-qv__add" data-bb-qv-add></button>' +
        "</div>" +
      "</div>";
    document.body.appendChild(qvEl);
    return qvEl;
  }

  /* Clear the src BEFORE setting the new one: the element is reused, so the
     previous product's image stays painted until the next one decodes. */
  function setQuickViewImage(src) {
    if (!qvEl) return;
    var img = qs("[data-bb-qv-img]", qvEl);
    var panel = qs(".bb-qv__panel", qvEl);
    if (!img) return;
    img.onload = null;
    img.onerror = null;
    img.removeAttribute("src");
    if (!src) {
      img.hidden = true;
      if (panel) panel.classList.remove("is-img-loading");
      return;
    }
    img.hidden = false;
    if (panel) panel.classList.add("is-img-loading");
    /* onerror too, or a dead URL leaves the panel stuck in the loading state. */
    img.onload = function () { if (panel) panel.classList.remove("is-img-loading"); };
    img.onerror = function () { if (panel) panel.classList.remove("is-img-loading"); };
    img.src = src;
  }

  function renderThumbs(el, urls, current) {
    var wrap = qs("[data-bb-qv-thumbs]", el);
    if (!wrap) return;
    /* One image is not a gallery: no row at all rather than an empty one. */
    if (urls.length < 2) {
      wrap.innerHTML = "";
      wrap.hidden = true;
      return;
    }
    var html = "";
    for (var i = 0; i < urls.length; i++) {
      var on = urls[i] === current;
      html +=
        '<button type="button" class="bb-qv__thumb' + (on ? " is-selected" : "") + '"' +
        ' data-bb-qv-thumb data-src="' + escapeHtml(urls[i]) + '"' +
        ' aria-current="' + (on ? "true" : "false") + '"' +
        ' aria-label="View image ' + (i + 1) + " of " + urls.length + '">' +
        '<img src="' + escapeHtml(thumbSrc(urls[i])) + '" alt="" loading="lazy">' +
        "</button>";
    }
    wrap.innerHTML = html;
    wrap.hidden = false;
  }

  function selectThumb(thumb) {
    if (!qvEl) return;
    var src = thumb.getAttribute("data-src") || "";
    if (!src) return;
    setQuickViewImage(src);
    qsa("[data-bb-qv-thumb]", qvEl).forEach(function (other) {
      var on = other === thumb;
      other.classList.toggle("is-selected", on);
      other.setAttribute("aria-current", on ? "true" : "false");
    });
  }

  function splitImgs(raw) {
    var out = [];
    var parts = String(raw || "").split(" ");
    for (var i = 0; i < parts.length; i++) {
      if (parts[i]) out.push(parts[i]);
    }
    return out;
  }

  /* A 54px thumbnail does not need a 640px file.

     snippets/bb-cart-addons.liquid builds data-bb-addon-imgs with
     `image_url: width: 640`, and those URLs did double duty: the gallery row
     rendered each one at 54 square, AND clicking one swaps it into the main
     image, which IS shown large. One list, two very different jobs — so
     opening a quick view fetched up to five 640px files to fill five 54px
     squares, which is most of what made it slow to appear.

     Shopify writes the rendition size as a `width=` query parameter, so the
     small version is the same URL with one number changed. That keeps ONE list
     in the Liquid: data-src below stays full size for the swap, and only the
     thumb's own src is shrunk. A second attribute would have been a second
     list to keep in step, for no gain.

     120 rather than 54 so the thumbs stay sharp on 2x screens.

     If the URL shape ever changes the regex simply does not match and the
     full-size URL is returned — the behaviour this replaced, so a miss is slow
     rather than broken. */
  function thumbSrc(url) {
    return String(url || "").replace(/([?&]width=)\d+/, "$1120");
  }

  function openQuickView(tile) {
    var srcBtn = qs(".bb-addon__btn", tile);
    if (!srcBtn) return;
    var el = buildQuickView();

    var urls = splitImgs(tile.getAttribute("data-bb-addon-imgs"));
    var mainSrc = tile.getAttribute("data-bb-addon-img") || "";
    if (!mainSrc && urls.length) mainSrc = urls[0];
    setQuickViewImage(mainSrc);
    renderThumbs(el, urls, mainSrc);

    qs("[data-bb-qv-title]", el).textContent = tile.getAttribute("data-bb-addon-title") || "";
    qs("[data-bb-qv-price]", el).textContent = tile.getAttribute("data-bb-addon-price") || "";
    qs("[data-bb-qv-desc]", el).textContent = tile.getAttribute("data-bb-addon-desc") || "";

    /* Parity with the tile: added is a live Remove, never a disabled dead end.
       Labels are static here rather than the tile's hover swap — this is the
       dialog's only primary action and touch has no hover to reveal it. */
    var added = tile.classList.contains("is-added");
    var act = qs("[data-bb-qv-add]", el);
    act.disabled = false;
    act.classList.remove("is-busy");
    act.classList.toggle("is-added", added);
    /* EVERY add-on hook is copied, not just box/variant/label. Miss one and a
       prebuilt add made from the quick view silently falls back to the builder
       shape and lands in no box at all. */
    [
      "data-bb-addon-box",
      "data-bb-addon-variant",
      "data-bb-addon-label",
      "data-bb-addon-kind",
      "data-bb-addon-grp",
      "data-bb-addon-bundle",
      "data-bb-addon-boxsku",
      "data-bb-addon-giftto",
      "data-bb-addon-boxkey"
    ].forEach(function (name) {
      act.setAttribute(name, srcBtn.getAttribute(name) || "");
    });
    if (added) {
      act.setAttribute("data-key", srcBtn.getAttribute("data-key") || "");
      act.textContent = "Remove from this box";
    } else {
      act.removeAttribute("data-key");
      act.textContent = "Add to this box";
    }

    /* Reused element: without this the next product opens at the previous
       one's scroll offset. */
    var qvBody = qs("[data-bb-qv-body]", el);
    if (qvBody) qvBody.scrollTop = 0;

    qvOpener = qs("[data-bb-addon-open]", tile) || tile;
    el.hidden = false;
    lockScroll();
    focusNoScroll(qs(".bb-qv__close", el));
  }

  function closeQuickView() {
    if (!qvEl || qvEl.hidden) return;
    qvEl.hidden = true;
    /* Unlock first: it restores the scroll position, then the focus move runs
       with preventScroll so it cannot undo that. */
    unlockScroll();
    focusNoScroll(qvOpener);
    qvOpener = null;
  }

  /* ----- add-on track arrows -----
     Whether the track overflows is a layout measurement, so Liquid renders the
     arrows hidden and this decides. No overflow -> they stay hidden, because an
     arrow pair with nothing to scroll to is worse than none. The scrollbar is
     hidden in CSS but the track is still a scroll container, so touch, trackpad
     and keyboard still work whether or not the arrows appear. */
  var navSyncing = false;

  function syncAddonNav() {
    /* Re-entrancy guard. Showing or hiding an arrow changes the flex width of
       the track, and with scroll-snap the browser can adjust scrollLeft and
       fire a scroll event straight back into here. */
    if (navSyncing) return;
    var wraps = qsa("[data-bb-addons-wrap]");
    if (!wraps.length) return;
    navSyncing = true;
    try {
      /* Read every measurement first, then write. Interleaving reads and
         writes forces a separate synchronous layout per wrap. */
      var plan = [];
      wraps.forEach(function (wrap) {
        var track = qs("[data-bb-addons-track]", wrap);
        var prev = qs("[data-bb-addons-prev]", wrap);
        var next = qs("[data-bb-addons-next]", wrap);
        if (!track || !prev || !next) return;
        var max = track.scrollWidth - track.clientWidth;
        plan.push({
          prev: prev,
          next: next,
          scrollable: max > 1,
          atStart: track.scrollLeft <= 1,
          atEnd: track.scrollLeft >= max - 1
        });
      });
      plan.forEach(function (p) {
        var hide = !p.scrollable;
        if (p.prev.hidden !== hide) p.prev.hidden = hide;
        if (p.next.hidden !== hide) p.next.hidden = hide;
        if (!p.scrollable) return;
        if (p.prev.disabled !== p.atStart) p.prev.disabled = p.atStart;
        if (p.next.disabled !== p.atEnd) p.next.disabled = p.atEnd;
      });
    } finally {
      navSyncing = false;
    }
  }

  /* rAF-debounced so a burst of calls costs one measure per frame. */
  var navFrame = 0;
  function queueAddonNav() {
    if (navFrame) return;
    navFrame = requestAnimationFrame(function () {
      navFrame = 0;
      syncAddonNav();
    });
  }

  /* Read the gap from CSS rather than hardcoding 8: the tile width formula in
     bb-cart-box-layout.css is built on --bb-addon-gap, and a second copy of the
     number here would silently desync the arrow step from the columns. */
  function trackGap(track) {
    try {
      var cs = window.getComputedStyle(track);
      var v = parseFloat(cs.columnGap);
      if (isNaN(v)) v = parseFloat(cs.gap);
      if (!isNaN(v)) return v;
    } catch (e) {}
    return 8;
  }

  /* ONE tile per click, not a page.
     A column is tile + gap, so stepping exactly one column always lands on a
     tile start -- which means every resting position is a snap point and the
     half-tile peek at the right edge is identical at every stop. Paging three
     at a time was disorienting: people expect the next product, not the next
     screenful.

     This also removes the fight with `scroll-snap-type: x mandatory`. Snapping
     is applied when a scroll operation finishes, so a smooth scrollBy only gets
     corrected if it lands somewhere that is not a snap point. Every target here
     is a tile start, and the clamped final position is covered by
     `.bb-addon:last-child { scroll-snap-align: end }`, so nothing is re-snapped
     after the animation. */
  function scrollAddons(wrap, dir) {
    var track = qs("[data-bb-addons-track]", wrap);
    if (!track) return;
    var tile = qs(".bb-addon", track);
    var step = (tile && tile.offsetWidth > 0)
      ? tile.offsetWidth + trackGap(track)
      : Math.round(track.clientWidth * 0.5);
    track.scrollBy({ left: dir * step, behavior: "smooth" });
  }

  /* scroll does not bubble, so this listens in the capture phase. */
  document.addEventListener("scroll", function (event) {
    var t = event.target;
    if (t && t.nodeType === 1 && t.hasAttribute && t.hasAttribute("data-bb-addons-track")) {
      queueAddonNav();
    }
  }, true);
  window.addEventListener("resize", queueAddonNav);
  window.addEventListener("load", queueAddonNav);

  /* NO MutationObserver here, deliberately. The previous version watched all of
     document.body (childList + subtree) and did a forced synchronous layout
     read (scrollWidth/clientWidth) in the callback, so every DOM change made by
     any app on this page — and it carries several — scheduled a reflow. The
     cart re-render is the only thing that needs a re-measure, and we always
     know when we caused one, so re-check on a short bounded schedule instead of
     watching the document. Four timers per cart action, then silence. */
  function scheduleNavRechecks() {
    var delays = [0, 150, 400, 900];
    for (var i = 0; i < delays.length; i++) {
      window.setTimeout(queueAddonNav, delays[i]);
    }
  }
  queueAddonNav();

  document.addEventListener("click", function (event) {
    var addBtn = event.target.closest("[data-bb-addon-add]");
    if (addBtn) {
      event.preventDefault();
      addToBox(addBtn);
      return;
    }

    /* Undo shares removeAddonLine with the quick view, so there is one removal
       path, and it renders in place instead of going through store.changeById. */
    var removeBtn = event.target.closest("[data-bb-addon-remove]");
    if (removeBtn) {
      event.preventDefault();
      if (removeBtn.disabled) return;
      removeAddonLine(removeBtn.getAttribute("data-key"), removeBtn);
      return;
    }

    var navBtn = event.target.closest("[data-bb-addons-prev], [data-bb-addons-next]");
    if (navBtn) {
      event.preventDefault();
      if (navBtn.disabled) return;
      var navWrap = navBtn.closest("[data-bb-addons-wrap]");
      if (navWrap) scrollAddons(navWrap, navBtn.hasAttribute("data-bb-addons-next") ? 1 : -1);
      return;
    }

    var openBtn = event.target.closest("[data-bb-addon-open]");
    if (openBtn) {
      event.preventDefault();
      var tile = openBtn.closest(".bb-addon");
      if (tile) openQuickView(tile);
      return;
    }

    var thumb = event.target.closest("[data-bb-qv-thumb]");
    if (thumb) {
      event.preventDefault();
      selectThumb(thumb);
      return;
    }

    if (event.target.closest("[data-bb-qv-close]")) {
      event.preventDefault();
      closeQuickView();
      return;
    }

    var qvAct = event.target.closest("[data-bb-qv-add]");
    if (qvAct) {
      event.preventDefault();
      var actAdded = qvAct.classList.contains("is-added");
      var actKey = qvAct.getAttribute("data-key") || "";
      var actOpts = addonOptsFromButton(qvAct);
      actOpts.btn = null;
      /* Close first whichever way this goes, as editCard/duplicateBox do: the
         cart re-renders underneath and a dialog left open over it is stale. */
      closeQuickView();
      if (actAdded) {
        /* Same single removal path as the tile. */
        removeAddonLine(actKey, null);
        return;
      }
      addAddonToBox(actOpts);
    }
  });

  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape" && qvEl && !qvEl.hidden) closeQuickView();
  });

  bindModal();
})();